import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { chatApi } from '@/lib/api/chat'
import { streamChatMessage } from '@/lib/api/chatStream'
import type {
  ChatConversation,
  CreateChatConversationRequest,
  SendChatMessageRequest,
} from '@/types'

const QUERY_KEYS = {
  conversations: (familyId: string) => ['chat', familyId, 'conversations'] as const,
  conversation: (familyId: string, conversationId: string) =>
    ['chat', familyId, 'conversations', conversationId] as const,
}

export function useChatConversations(familyId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.conversations(familyId ?? ''),
    queryFn: () => chatApi.listConversations(familyId!),
    enabled: !!familyId,
    staleTime: 30 * 1000,
  })
}

export function useChatConversation(familyId: string | null, conversationId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.conversation(familyId ?? '', conversationId ?? ''),
    queryFn: () => chatApi.getConversation(familyId!, conversationId!),
    enabled: !!familyId && !!conversationId,
    staleTime: 10 * 1000,
  })
}

export function useCreateChatConversation(familyId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateChatConversationRequest) =>
      chatApi.createConversation(familyId!, data),
    onSuccess: (conversation) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.conversations(familyId!) })
      queryClient.setQueryData(
        QUERY_KEYS.conversation(familyId!, conversation.id),
        conversation
      )
    },
  })
}

export function useSendChatMessage(familyId: string | null, conversationId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SendChatMessageRequest) =>
      chatApi.sendMessage(familyId!, conversationId!, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.conversations(familyId!) })
      queryClient.setQueryData(
        QUERY_KEYS.conversation(familyId!, response.conversation.id),
        response.conversation
      )
    },
  })
}

export interface ChatStreamState {
  /** Message text shown optimistically before/while the server echoes it. */
  pendingUserMessage: string | null
  /** Assistant text accumulated so far (empty while waiting for first token). */
  streamingContent: string
  isStreaming: boolean
  error: string | null
}

const IDLE_STREAM: ChatStreamState = {
  pendingUserMessage: null,
  streamingContent: '',
  isStreaming: false,
  error: null,
}

/**
 * Streaming message send with optimistic UI.
 *
 * The user's message appears instantly (pendingUserMessage), assistant
 * tokens accumulate in streamingContent, and on completion the query cache
 * is updated with the persisted conversation so local state can clear.
 */
export function useStreamChatMessage(familyId: string | null) {
  const queryClient = useQueryClient()
  const [state, setState] = useState<ChatStreamState>(IDLE_STREAM)

  const send = useCallback(
    async (conversationId: string, data: SendChatMessageRequest) => {
      if (!familyId) return
      setState({
        pendingUserMessage: data.message,
        streamingContent: '',
        isStreaming: true,
        error: null,
      })

      try {
        await streamChatMessage(familyId, conversationId, data, {
          onDelta: (content) =>
            setState((s) => ({ ...s, streamingContent: s.streamingContent + content })),
          onDone: (response) => {
            queryClient.setQueryData<ChatConversation>(
              QUERY_KEYS.conversation(familyId, response.conversation.id),
              response.conversation
            )
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.conversations(familyId) })
            setState(IDLE_STREAM)
          },
        })
        // Stream ended without a done event (connection dropped): refetch.
        setState((s) => {
          if (s.isStreaming) {
            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.conversation(familyId, conversationId),
            })
            return IDLE_STREAM
          }
          return s
        })
      } catch (error) {
        setState((s) => ({
          ...s,
          isStreaming: false,
          error: error instanceof Error ? error.message : 'Chat request failed',
        }))
        // The user message may have been saved before the failure.
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.conversation(familyId, conversationId),
        })
      }
    },
    [familyId, queryClient]
  )

  const clearError = useCallback(() => setState(IDLE_STREAM), [])

  return { ...state, send, clearError }
}
